import {Button, Container, Group, SegmentedControl, Stack, Stepper, Text, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CustomerSelector} from '../components/CustomerSelector';
import {SubscriptionSelector} from '../components/SubscriptionSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult, extractErrorMessage} from '../types';
import {update as updateSubscription, deleteRecord as deleteSubscription} from '../../subscriptions/services/SubscriptionsService';
import {Subscription} from '../../subscriptions/types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const CancelSubscriptionPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
    const [cancelMode, setCancelMode] = useState<string>('at_period_end');
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const handleCancel = async () => {
        if (!subscriptionId) return;
        setProcessing(true);
        try {
            if (cancelMode === 'immediately') {
                await deleteSubscription(subscriptionId);
                console.log('Subscription deleted:', subscriptionId);
                notifySuccess('Subscription cancelled immediately');
                setResult({
                    success: true,
                    subscriptionId,
                    status: 'canceled',
                });
            } else {
                const sub = await updateSubscription(subscriptionId, {id: subscriptionId, cancelAtPeriodEnd: true} as unknown as Subscription);
                console.log('Subscription set to cancel at period end:', sub);
                notifySuccess('Subscription will cancel at period end');
                setResult({
                    success: true,
                    subscriptionId,
                    status: (sub as Subscription)?.status,
                });
            }
            setActive(1);
        } catch (e) {
            notifyError(e);
            setResult({success: false, error: extractErrorMessage(e)});
            setActive(1);
        } finally {
            setProcessing(false);
        }
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setSubscriptionId(null);
        setCancelMode('at_period_end');
        setResult(null);
    };

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>Cancel Subscription</Title>
                    <Text c="dimmed" size="sm">
                        Cancel an active subscription immediately or at the end of the billing period.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Select Subscription"/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={(v) => { setCustomerId(v); setSubscriptionId(null); }}/>
                        <SubscriptionSelector value={subscriptionId} onChange={setSubscriptionId} customerId={customerId} activeOnly/>
                        <div>
                            <Text size="sm" fw={500} mb="xs">Cancellation Mode</Text>
                            <SegmentedControl
                                value={cancelMode}
                                onChange={setCancelMode}
                                data={[
                                    {label: 'At Period End', value: 'at_period_end'},
                                    {label: 'Immediately', value: 'immediately'},
                                ]}
                            />
                        </div>
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>Back</Button>
                            <Button
                                color="red"
                                onClick={handleCancel}
                                disabled={!subscriptionId}
                                loading={processing}
                            >
                                Cancel Subscription
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
